import { flatten, pick, sum } from "lodash-es"
import qs from "query-string"
import React from "react"
import { useNavigate } from "react-router-dom"
import CustomTag from "components/Atoms/CustomTag"
import StyledLink from "components/Atoms/StyledLink"
import Error from "components/Common/Error"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import CustomReactTable from "components/Templates/CustomReactTable"
import PopoverTooltip from "components/Templates/PopoverTooltip"
import { useModuleDcs } from "graphql/order/order.hooks"
import { useSellableItemStockList } from "graphql/sellable-item/sellable-item.hooks"
import { useFilter } from "store/search-filter/search-filter.reducer"
import { getId } from "utils/custom.utils"
import mergeSum from "utils/mergeSum"
import unique from "utils/unqiue"
import StockRange from "./StockRange"
import ROUTES from "constants/routes.constants"
const StockTable = () => {
  const [filter] = useFilter("ecommerce")
  const dcs = useModuleDcs("ecommerce")
  const navigate = useNavigate()
  const multipleDcs = dcs?.length > 1

  const COLUMNS = [
    {
      width: 50,
      accessor: "sellableItem.id",
      aggregate: () => null,
      Aggregated: () => null,
      Cell: ({ index }) => index + 1,
    },
    {
      width: 300,
      Header: "Sellable Item",
      accessor: "sellableItem",
      aggregate(values) {
        return values?.[0]
      },
      Aggregated({ value }) {
        const search = qs.stringify({
          id: value?.id,
          search: value?.codename,
        })
        return (
          <StyledLink
            to={{
              pathname: "/d/ecom/stock/sellable-items",
              search,
            }}
          >
            {value?.name}
          </StyledLink>
        )
      },
      Cell({ value, original }) {
        const search = qs.stringify({
          id: value?.id,
          search: value?.codename,
        })
        const isLive = original?.isLive
        return multipleDcs ? (
          <div>
            <CustomTag
              small
              round
              minimal
              intent={isLive ? "success" : "danger"}
            >
              {isLive ? "Live" : "Not Live"}
            </CustomTag>
          </div>
        ) : (
          <div>
            <StyledLink
              to={{
                pathname: "/d/ecom/stock/sellable-items",
                search,
              }}
            >
              {value?.name}
            </StyledLink>
            <div>
              <CustomTag
                small
                round
                minimal
                intent={isLive ? "success" : "danger"}
              >
                {isLive ? "Live" : "Not Live"}
              </CustomTag>
            </div>
          </div>
        )
      },
    },

    {
      Header: "Groups Visible",
      accessor: "groupVisibility",
      aggregate: values => unique(flatten(values)),
      Aggregated({ value }) {
        return <OverFlowMenu items={value} />
      },
      Cell({ value }) {
        return <OverFlowMenu items={value} />
      },
    },
    {
      width: multipleDcs ? 200 : 120,
      Header: "DC Name",
      accessor: "distributionCentre",
      aggregate: values => unique(flatten(values)),
      Aggregated({ value }) {
        return (
          <OverFlowMenu
            icon="office"
            onClick={i => {
              navigate(ROUTES.dcDetail(i?.id))
            }}
            items={value}
          />
        )
      },
      Cell({ value }) {
        return (
          <StyledLink to={ROUTES.dcDetail(value?.id)}>{value?.name}</StyledLink>
        )
      },
    },
    {
      width: 100,
      Header: "DC Address",
      accessor: "distributionCentre.address",
      aggregate: _ => null,
      Aggregated: _ => null,
      Cell({ value }) {
        const formatted = value?.address?.split(",")
        return (
          <div className="vt-text-left">
            <PopoverTooltip
              trigger={
                <div style={{ cursor: "pointer" }}>
                  <div className="vt-muted">{value?.village}</div>
                </div>
              }
            >
              <div className="p-2 text-xs">
                <div className="text-sm vt-black mb-1">Address Details</div>
                {formatted?.map((al, idx) => {
                  return (
                    <p key={idx} className="mb-0">
                      {al},
                    </p>
                  )
                })}
              </div>
            </PopoverTooltip>
          </div>
        )
      },
    },
    {
      id: "stocks",
      width: 300,
      Header: "Stock Count",
      aggregate: mergeSum,
      Aggregated({ value }) {
        return (
          <StockRange
            bs={value?.bookedStockCount}
            rs={value?.rejectedStockCount}
            hs={value?.holdStockCount}
            cs={value?.currentStockCount}
            os={value?.offerStockCount}
          />
        )
      },
      accessor(stock) {
        return pick(stock, [
          "holdStockCount",
          "currentStockCount",
          "offerStockCount",
          "bookedStockCount",
          "rejectedStockCount",
        ])
      },

      Cell({ value }) {
        return (
          <StockRange
            bs={value?.bookedStockCount}
            rs={value?.rejectedStockCount}
            hs={value?.holdStockCount}
            cs={value?.currentStockCount}
            os={value?.offerStockCount}
          />
        )
      },
    },
    {
      maxWidth: 120,
      Header: "Total Stock",
      accessor: "totalStockCount",
      aggregate: values => sum(values),
    },
    {
      maxWidth: 120,
      Header: "Booked Stock",
      accessor: "bookedStockCount",
      aggregate: values => sum(values),
      Aggregated: ({ value }) => value,
    },
    {
      maxWidth: 120,
      Header: "Display Stock",
      accessor: "offerStockCount",
      aggregate: values => sum(values),
      Aggregated: ({ value }) => value,
    },
    {
      Header: "Description",
      accessor: "sellableItem.shortDescription",
      aggregate: values => values?.[0],
      Aggregated: ({ value }) => value,
      Cell: ({ value }) => (dcs?.length > 1 ? null : value),
    },
  ]
  const { data, loading, error } = useSellableItemStockList({
    distributionCentres: dcs?.map(getId),
    isLive: filter?.isLive,
  })
  const items = data?.sellableItemStock

  if (error) return <Error message="Unable to fetch the result" />

  return (
    <div>
      <CustomReactTable
        pivotBy={multipleDcs ? ["sellableItem.id"] : []}
        pageSize={15}
        leftAlign
        loading={loading}
        data={items}
        columns={COLUMNS}
      />
    </div>
  )
}

export default StockTable
